/**
 * What the trip log says about journeys driven more than once.
 *
 * The driver's question: "I drive the same route quite a few times - which way
 * is actually better and faster?" Between two provinces there is usually more
 * than one sensible road, and the router will not tell you which of them YOUR
 * drives were quicker on. The trip log already knows: every drive carries its
 * duration, moving time, signals, reroutes and a recorded track.
 *
 * The analysis is three steps:
 *
 *  1. GROUP journeys that are the same trip - started near each other and
 *     ended near each other. Endpoint proximity, not names: "Surin" typed one
 *     day and a pinned shop in Surin the next are the same journey.
 *  2. Within a group, split the drives into ROUTES by the corridor their track
 *     ran through. Two drives that took different roads are different routes
 *     even though the endpoints match - that is the whole point.
 *  3. Compare the routes on what was actually recorded.
 *
 * What this deliberately does NOT do: declare a winner on a single fast drive.
 * One clear run at 6 am beats the same road in Friday traffic, so a route needs
 * repeated evidence before it is called better, and the comparison reports how
 * much evidence it has. See `confident`.
 */

import type { LngLat, Trip } from '../core/types';
import { distance } from '../core/geo';
import { roadRefsFromStoredLabel } from './roads';
import { readPoint } from './tripRecorder';

/** Two journeys are "the same" if their starts are within this of each other. */
const SAME_ORIGIN_M = 20_000;
/** ...and their ends within this. Tighter: the destination is the point. */
const SAME_DEST_M = 5_000;
/**
 * Two tracks belong to the same route if their midpoints are this close.
 * Wide enough to absorb lane-level and roadworks detours, tight enough to
 * separate genuinely different corridors (Prasat/24 vs Krasang/Prakhon Chai
 * are tens of km apart at the midpoint).
 */
const SAME_ROUTE_M = 6_000;
/** A track shorter than this cannot characterise a corridor. */
const MIN_TRACK_POINTS = 8;
/** Journeys shorter than this are errands, not routes worth comparing. */
const MIN_JOURNEY_M = 15_000;
/** Sample points along a track used as its corridor fingerprint. */
const FINGERPRINT_FRACTIONS = [0.25, 0.5, 0.75];

/** One way of making a journey, as actually driven. */
export interface RouteVariant {
  /** Stable id: the trip that first defined this corridor. */
  id: string;
  /** Every drive that took this corridor, newest first. */
  trips: Trip[];
  /** Median duration in seconds - median, not mean, so one jam cannot skew it. */
  medianDuration: number;
  /** Median distance in metres. */
  medianDistance: number;
  /** Mean moving speed in km/h over drives that recorded it. */
  avgSpeed: number | null;
  /** Mean seconds stopped per drive (lights, queues) where known. */
  avgStopped: number | null;
  /** Mean traffic signals encountered. */
  avgSignals: number | null;
  /** Numbered roads this corridor is made of, most-travelled first. */
  roads: string[];
  /** When it was last driven. */
  lastDrivenAt: number;
  /** Corridor fingerprint, for matching a live route against this variant. */
  fingerprint: LngLat[];
}

/** A journey driven more than once, and the ways it has been driven. */
export interface JourneyReport {
  id: string;
  originName: string;
  destinationName: string;
  origin: LngLat;
  destination: LngLat;
  /** Every drive of this journey, newest first. */
  trips: Trip[];
  /** The distinct corridors, fastest (by median) first. */
  variants: RouteVariant[];
  /** The fastest variant, when there is more than one to choose between. */
  best: RouteVariant | null;
  /** Median seconds the best variant saves over the slowest. 0 when only one. */
  savingSeconds: number;
  /**
   * True when the comparison rests on enough drives to mean something: at
   * least two variants, each driven at least twice. Below that the report
   * still shows, but as history rather than a recommendation.
   */
  confident: boolean;
}

function trackPoints(trip: Trip): LngLat[] {
  return (trip.track ?? []).map((p) => {
    const q = readPoint(p);
    return [q[0], q[1]] as LngLat;
  });
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2);
}

function mean(values: number[]): number | null {
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/** Points at fixed fractions along the track - the corridor's shape in three numbers. */
function fingerprint(trip: Trip): LngLat[] {
  const pts = trackPoints(trip);
  if (pts.length < MIN_TRACK_POINTS) return [];
  return FINGERPRINT_FRACTIONS.map((f) => pts[Math.floor(pts.length * f)]).filter(
    (p): p is LngLat => Array.isArray(p),
  );
}

/** Two corridors are the same when every sample point is close to its pair. */
function sameCorridor(a: LngLat[], b: LngLat[]): boolean {
  if (!a.length || a.length !== b.length) return false;
  return a.every((p, i) => distance(p, b[i]) < SAME_ROUTE_M);
}

function originOf(trip: Trip): LngLat | null {
  if (trip.origin) return trip.origin;
  const pts = trackPoints(trip);
  return pts[0] ?? null;
}

/** The numbered roads a drive spent the most distance on. */
function roadsOf(trips: Trip[]): string[] {
  const byRoad = new Map<string, number>();
  for (const trip of trips) {
    for (const seg of trip.segments ?? []) {
      for (const road of roadRefsFromStoredLabel(seg.road ?? '')) {
        byRoad.set(road, (byRoad.get(road) ?? 0) + seg.distance);
      }
    }
  }
  return [...byRoad.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([road]) => road);
}

/** Group drives of the same journey into corridors, and summarise each. */
function buildVariants(trips: Trip[]): RouteVariant[] {
  const groups: Array<{ fp: LngLat[]; trips: Trip[] }> = [];

  for (const trip of trips) {
    const fp = fingerprint(trip);
    if (!fp.length) continue;
    const hit = groups.find((g) => sameCorridor(g.fp, fp));
    if (hit) hit.trips.push(trip);
    else groups.push({ fp, trips: [trip] });
  }

  return groups.map((g) => {
    const sorted = [...g.trips].sort((a, b) => b.startedAt - a.startedAt);
    const stopped = sorted
      .filter((x) => x.movingSeconds != null)
      .map((x) => Math.max(0, x.duration - x.movingSeconds!));
    return {
      id: sorted[sorted.length - 1].id,
      trips: sorted,
      medianDuration: median(sorted.map((x) => x.duration).filter((d) => d > 0)),
      medianDistance: median(sorted.map((x) => x.distance).filter((d) => d > 0)),
      avgSpeed: mean(sorted.map((x) => x.avgSpeed).filter((s): s is number => s != null)),
      avgStopped: mean(stopped),
      avgSignals: mean(sorted.map((x) => x.trafficSignals).filter((n): n is number => n != null)),
      roads: roadsOf(sorted),
      lastDrivenAt: sorted[0].startedAt,
      fingerprint: g.fp,
    };
  });
}

/**
 * Journeys this driver has made more than once, newest activity first.
 *
 * `minDrives` is how many drives a journey needs before it is reported at all;
 * two is enough to be a repeat, but the comparison only becomes a
 * recommendation once `confident` is true.
 */
export function analyseJourneys(trips: Trip[], minDrives = 2): JourneyReport[] {
  const usable = trips.filter(
    (t) => t.destination && t.distance >= MIN_JOURNEY_M && t.duration > 0 && originOf(t),
  );

  const groups: Array<{ origin: LngLat; destination: LngLat; trips: Trip[] }> = [];
  for (const trip of usable) {
    const origin = originOf(trip)!;
    const hit = groups.find(
      (g) =>
        distance(g.origin, origin) < SAME_ORIGIN_M &&
        distance(g.destination, trip.destination) < SAME_DEST_M,
    );
    if (hit) hit.trips.push(trip);
    else groups.push({ origin, destination: trip.destination, trips: [trip] });
  }

  const reports: JourneyReport[] = [];
  for (const g of groups) {
    if (g.trips.length < minDrives) continue;
    const byDate = [...g.trips].sort((a, b) => b.startedAt - a.startedAt);
    const variants = buildVariants(byDate).sort((a, b) => a.medianDuration - b.medianDuration);
    if (!variants.length) continue;

    // Only variants with real repetition can be called better or worse; a
    // single drive is an anecdote.
    const repeated = variants.filter((v) => v.trips.length >= 2);
    const comparable = repeated.length >= 2;
    const best = variants.length > 1 ? variants[0] : null;
    const saving = comparable
      ? repeated[repeated.length - 1].medianDuration - repeated[0].medianDuration
      : 0;

    reports.push({
      id: byDate[0].id,
      originName: byDate[0].originName || '—',
      destinationName: byDate[0].destinationName || '—',
      origin: g.origin,
      destination: g.destination,
      trips: byDate,
      variants,
      best,
      savingSeconds: Math.max(0, saving),
      confident: comparable,
    });
  }

  return reports.sort((a, b) => b.trips[0].startedAt - a.trips[0].startedAt);
}

/**
 * The journey report covering a live origin/destination, if there is one.
 *
 * Used to tell the driver, while planning, that they have driven this before
 * and which way was quicker.
 */
export function reportFor(
  reports: JourneyReport[],
  origin: LngLat,
  destination: LngLat,
): JourneyReport | null {
  return (
    reports.find(
      (r) =>
        distance(r.origin, origin) < SAME_ORIGIN_M &&
        distance(r.destination, destination) < SAME_DEST_M,
    ) ?? null
  );
}
